import * as React from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { OrganizationStage } from '@gcv/shared';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, RadioGroup } from '@mui/material';

import { Error, Header, RadioButton, Text } from 'ui';
import palette from 'ui/theme/palette';

interface Props {
  form: UseFormReturn<FieldValues, any>;
  isMobile: boolean;
  onChange: () => void;
  serviceProviderName: string;
}

const InfoBoxStyles = {
  display: 'flex',
  gap: 1,
  padding: 2,
  marginY: 1,
  border: 1,
  borderColor: palette.border,
  borderRadius: 2,
  backgroundColor: palette.info.screen
};

export const OrganizationStageCard: React.FC<Props> = (props) => {
  const header = (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignContent: 'center', alignItems: 'center' }}>
        <Header mobileHeader={props.isMobile} content="License & Operations Status" type="h2" />
      </Box>
      <Box sx={InfoBoxStyles}>
        <InfoOutlined sx={{ fontSize: '16px', color: palette.secondary.main }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <Text content={`Why we're asking`} type="table-header" />
          <Text
            content={`${props.serviceProviderName} indicated this account is for a cannabis business. This helps us understand your license and operational status so we can set up proper monitoring and documentation requirements.`}
            type="body2"
          />
        </Box>
      </Box>
    </>
  );

  const radioOptions: { value: string; title: string; subtitle: string }[] = [
    {
      value: OrganizationStage.PENDING_LICENSE,
      title: 'Not licensed yet (or I have a provisional/"seedling" license)',
      subtitle: `Select this if you're still in the application process or have a conditional/provisional license but haven't received your final operation license yet. You'll be enrolled in monthly status checking-ins.`
    },
    {
      value: OrganizationStage.LICENSED_BUT_NOT_OPERATIONAL,
      title: `Licensed but not operating yet`,
      subtitle: `You've received your final cannabis license but haven't started selling or plant-touching operations yet. You're setting up facilities, hiring staff, or preparing to launch.`
    },
    {
      value: OrganizationStage.ACTIVE,
      title: `Licensed and actively operating`,
      subtitle: `You have your final license and are currently conducting plant-touching operations (growing, processing, dispensing, or selling cannabis products).`
    }
  ];

  const licenseStatusRadioGroup = (
    <>
      <Box
        sx={{
          display: 'flex'
        }}
      >
        <Header
          mobileHeader={props.isMobile}
          content="Which option best describes your business right now? *"
          type="h3"
        />
      </Box>
      <Controller
        control={props.form.control}
        name={'organization_stage'}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <RadioGroup
            onChange={(event) => {
              onChange(event.target.value);
            }}
            sx={{ display: 'flex', gap: 2 }}
          >
            {error && <Error content={'* License status is required'} />}
            {radioOptions.map((option) => {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    padding: 2,
                    gap: 1,
                    border: 2,
                    borderColor: option.value === value ? palette.primary.border : palette.border,
                    backgroundColor: option.value === value ? palette.primary.screen : undefined,
                    borderRadius: 2,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (value !== option.value) {
                      props.form.setValue('organization_stage', option.value);
                      props.onChange();
                    }
                  }}
                >
                  <RadioButton value={option.value} checked={option.value === value} />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <Text content={option.title} type="table-header" />
                    <Text content={option.subtitle} type="body2" />
                  </Box>
                </Box>
              );
            })}
          </RadioGroup>
        )}
      />
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        marginTop: 1
      }}
      data-cy="organization-stage-card"
    >
      {header}
      {licenseStatusRadioGroup}
    </Box>
  );
};
